import type {CopilotSpaceResource, CopilotSpaceRepositoryBackedResource} from '../types'
import type {OwnerItem} from '../components/SpacesOwnerDropdown'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'

interface ResourcesForDuplicationResult {
  resourcesToSave: CopilotSpaceResource[]
  resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[]
}

/**
 * Determines which resources can be saved when duplicating a space to a new owner
 * and which will not transfer due to access restrictions.
 *
 * Private resources owned by a different organization/user than the target owner
 * will not be transferable if the target owner is a restricted organization or
 * spaces_org_scoping is enabled for a personal space.
 *
 * Opted-in organizations and personal spaces without scoping retain all resources.
 *
 * @param resources - The resources from the original space
 * @param targetOwner - The new owner for the duplicated space
 * @returns An object containing resources that can be saved and those that cannot transfer
 */
export function resourcesForDuplication(
  resources: CopilotSpaceResource[],
  targetOwner: OwnerItem,
): ResourcesForDuplicationResult {
  const resourcesToSave: CopilotSpaceResource[] = []
  const resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[] = []

  const restricted =
    targetOwner.type === 'Organization'
      ? !targetOwner.allowsPrivateReposFromOtherOrgs
      : copilotFeatureFlags.spacesOrgScoping

  if (restricted) {
    for (const resource of resources) {
      // Check if this is a repository-backed resource with ownership and privacy info
      if ('ownerId' in resource && 'private' in resource && resource.private && resource.ownerId !== targetOwner.id) {
        // Private resource owned by different owner - cannot transfer
        resourcesThatWillNotTransfer.push(resource)
      } else {
        // Resource can be saved
        resourcesToSave.push(resource)
      }
    }

    return {
      resourcesToSave,
      resourcesThatWillNotTransfer,
    }
  } else {
    return {
      resourcesToSave: resources,
      resourcesThatWillNotTransfer: [],
    }
  }
}
